'use client'
import * as React from 'react'
import { Star, ShoppingBag } from 'lucide-react'
import { SafeImage } from '@/components/marketing/safe-image'
import { cn } from '@/lib/utils'
import { brandGradientFor, starsFor, type FormatProduct } from './format-data'

/**
 * Reduced-motion subscription. Engines read this to gate auto-advance,
 * ken-burns, marquee, and pulsing pins. Returns true when the user has asked
 * for reduced motion (also true during SSR/first paint to be safe).
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState(true)
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const apply = () => setReduced(mq.matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])
  return reduced
}

/**
 * Lazy-mount gate. Renders `placeholder` until the wrapper scrolls into view,
 * then mounts `children` once. Keeps off-screen engines out of the work
 * budget (no autoplay video, no timers) until they matter.
 */
export function InViewMount({
  children,
  placeholder,
  className,
  rootMargin = '200px',
}: {
  children: React.ReactNode
  placeholder?: React.ReactNode
  className?: string
  rootMargin?: string
}) {
  const ref = React.useRef<HTMLDivElement>(null)
  const [shown, setShown] = React.useState(false)
  React.useEffect(() => {
    const el = ref.current
    if (!el || shown) return
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShown(true)
            io.disconnect()
          }
        }
      },
      { rootMargin },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [shown, rootMargin])
  return (
    <div ref={ref} className={className}>
      {shown ? children : placeholder ?? null}
    </div>
  )
}

/** Star rating row. Cobalt-tinted, accessible label. */
export function Stars({ rating, className }: { rating: number; className?: string }) {
  const { full, half } = starsFor(rating)
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-primary', className)} aria-label={`${rating} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className="h-3.5 w-3.5"
          fill={i < full || (i === full && half) ? 'currentColor' : 'none'}
          strokeWidth={1.5}
          aria-hidden
        />
      ))}
    </span>
  )
}

/**
 * Brand-backed media frame. Renders the real image (SafeImage, with a stable
 * picsum fallback) when `src` is set; otherwise a deterministic brand gradient
 * so an image-less slot still reads as an intentional brand surface — never a
 * fabricated photo. `kenBurns` is gated on reduced-motion by the caller.
 */
export function MediaFrame({
  src,
  alt,
  seed,
  slug,
  kenBurns,
  className,
  sizes,
  priority,
}: {
  src?: string
  alt: string
  seed?: number
  slug: string
  kenBurns?: boolean
  className?: string
  sizes?: string
  priority?: boolean
}) {
  return (
    <div className={cn('absolute inset-0 overflow-hidden', className)} aria-hidden={!src}>
      {src ? (
        <SafeImage
          src={src}
          alt={alt}
          fill
          priority={priority}
          sizes={sizes ?? '(max-width: 768px) 100vw, 50vw'}
          fallbackSeed={`${slug}-${seed ?? 0}`}
          className={cn(
            'object-cover',
            kenBurns && 'motion-safe:animate-[fmt-kenburns_22s_ease-in-out_infinite_alternate]',
          )}
        />
      ) : (
        <div className="absolute inset-0" style={{ backgroundImage: brandGradientFor(slug, seed ?? 0) }} />
      )}
    </div>
  )
}

/** Small square product thumbnail used in rails / cards. */
export function ProductThumb({
  product,
  slug,
  seed,
  size = 56,
}: {
  product: FormatProduct
  slug: string
  seed?: number
  size?: number
}) {
  return (
    <span
      className="relative flex-none overflow-hidden rounded-[var(--r-sm)] bg-secondary"
      style={{ width: size, height: Math.round(size * 1.16) }}
    >
      <SafeImage
        src={product.image}
        alt={product.name}
        fill
        sizes={`${size}px`}
        fallbackSeed={`${slug}-prod-${seed ?? product.id}`}
        className="object-cover"
      />
    </span>
  )
}

/** Cobalt "Add" pill used across rails + cards. */
export function AddPill({
  onClick,
  label = 'Add',
  added,
  className,
}: {
  onClick: () => void
  label?: string
  added?: boolean
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        // AddPill is frequently placed inside a larger clickable row (see
        // shop-the-look.tsx, social-gallery.tsx, lookbook-carousel.tsx,
        // shoppable-story.tsx). Stop the click from also firing the row's
        // own handler regardless of whether that row is a real <button> or
        // a div-with-role — nested interactive controls must never be a
        // prerequisite for this to work correctly.
        e.stopPropagation()
        onClick()
      }}
      className={cn(
        'inline-flex flex-none items-center gap-1.5 rounded-[var(--r-pill)] px-3.5 py-2 text-xs font-semibold',
        'transition-[background-color,transform] duration-[var(--dur-fast)] ease-[var(--ease-out)]',
        'motion-safe:active:scale-[0.96] motion-reduce:transition-none',
        'focus-visible:outline-none focus-visible:[box-shadow:var(--ring)]',
        added
          ? 'bg-emerald-600 text-white'
          : 'bg-[var(--accent)] text-[var(--fg-on-accent)] hover:bg-[var(--accent-hover)]',
        className,
      )}
    >
      <ShoppingBag className="h-3.5 w-3.5" aria-hidden />
      {added ? 'Added' : label}
    </button>
  )
}

/** Hook for the ephemeral "Added" toast state on Add pills. */
export function useAdded(): [string | null, (id: string) => void] {
  const [added, setAdded] = React.useState<string | null>(null)
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const fire = React.useCallback((id: string) => {
    setAdded(id)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => setAdded(null), 1400)
  }, [])
  React.useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])
  return [added, fire]
}
